'use client'

import { Box, Flex, HoverCard, SimpleGrid, Tabs, Text } from '@mantine/core'
import { useViewportSize } from '@mantine/hooks'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import ChevronDownIcon from '@/assets/icons/chevron-down.svg'
import ChevronRightIcon from '@/assets/icons/chevron-right.svg'
import LiveIcon from '@/assets/icons/live-broadcast.svg'
import NextBaseImage from '@/components/basic/BaseImage'
import { useIsResponsiveMobileView } from '@/hooks/responsive'

import styles from './styles.module.css'

const DEFAULT_MAX_VISIBLE_COUNT = 7

/**
 * 每条逻辑 nav 条(scrollTrackKey)跨重挂载持久的状态。模块级缓存只在整页刷新
 * (JS 模块重载)时清空,因此是「仅真正首次加载才首滚」的可靠判据。
 *
 * cdc-market 的 prediction 区虽不做 URL 软导航,但选中 chip 变化会重渲染整个
 * section,key 化的记忆保证「首滚一次 + 停留位置不被选中变化打断」。
 */
interface ScrollMemo {
  /** 是否已完成首次定位滚动(整页加载时)。 */
  scrolledOnce: boolean
  /** 最近一次的横向滚动位置,重挂载时据此恢复停留位置。 */
  scrollLeft: number
}
const scrollMemosByTrackKey = new Map<string, ScrollMemo>()

export interface NavChipListItem {
  /** Stable id for keying / matching active state. */
  slug: string
  label: string
  isActive: boolean
  onClick?: () => void
  /** Small muted label after the title, e.g. "660" or "200+" for the Live chip. */
  countLabel?: string
  /** Optional 16px circular icon shown before the label, always full-color. */
  iconUrl?: string
  /**
   * When true, the chip renders as the Live pill (Figma 8432:136443): a red
   * broadcast glyph (16px, #E0485C, always on) leads the title-case "Live"
   * label; the label follows the normal chip state colors.
   */
  showLiveDot?: boolean
}

interface NavChipListProps {
  items: NavChipListItem[]
  /** When true, render every chip and let them wrap onto multiple rows. */
  showAllChips?: boolean
  maxVisibleCount?: number
  /**
   * Mobile chip look. `'underline'` (default) keeps the shared pill styling;
   * `'pill'` swaps to the compact content-adaptive pill chip used by the
   * mobile L2 strip (`SecondLevelNavTree`) — outlined/filled, active state
   * carried by background + border instead of the L1 active colors.
   */
  mobileVariant?: 'underline' | 'pill'
  ariaLabel?: string
  /**
   * 溢出导航模式:所有 chip 渲染成单行,不换行、不截断,也不出 "More" 下拉;
   * 内容超宽时在两侧显示 left/right 箭头逐 chip 横向滚动。箭头仅在"该方向还可
   * 滚动"时可见(起点/终点或无溢出时不占位,见 :disabled 样式);默认 false
   * 保持桌面截断+More / 移动触摸滚动的行为。
   */
  scrollArrows?: boolean
  /**
   * Mobile-only inline-start padding (px) reserved on the scroll strip for a
   * caller-rendered overlay pinned over its leading edge. 0 by default.
   */
  mobileLeadingInset?: number
  /**
   * 可选的跨重挂载持久 key。提供 key 时,模块级内存会记住「是否已完成首滚」
   * 与最近滚动位置:
   *  - 整页刷新 / 首次挂载:key 无存档 → 只做一次首滚(选中 chip 滚入可视区);
   *  - 重挂载:跳过首滚并恢复上次停留的滚动位置。
   * 不传此 prop 的用法退回组件实例级首滚(挂载即滚一次,无跨挂载记忆)。
   */
  scrollTrackKey?: string
  /**
   * 外部驱动的「滚到选中 chip」触发信号(如抽屉里选中 L2 后收起)。每次值
   * 变化都强制把当前 `data-active` chip 滚入可视区左侧(行为 smooth)。
   * 与 scrollTrackKey 的「只首滚一次 + 保留停留位置」互不干扰——这是主动
   * 追位,用户手动滚动后会照常被 scroll 记忆覆盖。
   */
  scrollToActiveTrigger?: number
}

/**
 * L1 chip strip, restyled to the Figma pill design (node 8432:136438):
 * 36px pills, radius 40px, selected = blue fill + blue border + active-bold
 * label, Live chip leads with a red broadcast glyph (no uppercase, no dot).
 *
 * Layout:
 *  - Mobile: horizontal scroll, no overflow dropdown.
 *  - Desktop (default): chips clipped to one row, overflow goes into a "More" dropdown.
 *  - Desktop (`showAllChips`): chips wrap to multiple rows, no More dropdown.
 *
 * Port of cdc-web `NavChipListV2`. Removed: framer-motion shared-layout
 * underline, View Transitions slide, Link/href routing (chips are buttons
 * driven by `onClick`), light-mode asset transform, and the pill mobile
 * variant (mobile L2 strip is out of scope).
 */
export default function NavChipList({
  items,
  showAllChips = false,
  maxVisibleCount = DEFAULT_MAX_VISIBLE_COUNT,
  mobileVariant = 'underline',
  ariaLabel,
  scrollArrows = false,
  mobileLeadingInset = 0,
  scrollTrackKey,
  scrollToActiveTrigger,
}: Readonly<NavChipListProps>) {
  const t = useTranslations('HomePageV2.Prediction')
  const containerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(maxVisibleCount)
  const { width } = useViewportSize()
  const isMobile = useIsResponsiveMobileView()

  const activeValue = useMemo(() => items.find(i => i.isActive)?.slug ?? null, [items])

  // 无 scrollTrackKey 用法下的组件实例级「已首滚」标记(挂载即滚一次)。带 key
  // 的用法走模块级 scrollMemosByTrackKey —— 重挂载会重置组件级 ref,跨挂载的
  // 「只滚一次 + 恢复停留位置」由 key 存档承担。
  const didInitialScrollRef = useRef(false)

  const adjustLayout = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const children = Array.from(container.children) as HTMLElement[]
    if (children.length === 0) return

    // scrollArrows 模式同样渲染全部 chip(单行,靠滚动而不是截断/换行)。
    if (showAllChips || scrollArrows) {
      children.forEach(ch => {
        ch.style.display = ''
      })
      setVisibleCount(items.length)
      return
    }

    const itemsToCheck = Math.min(children.length, maxVisibleCount)

    for (let i = 0; i < itemsToCheck; i++) {
      children[i].style.display = ''
    }

    const firstTop = children[0].offsetTop
    let count = 1
    for (let i = 1; i < itemsToCheck; i++) {
      if (children[i].offsetTop > firstTop) break
      count++
    }

    for (let i = count; i < itemsToCheck; i++) {
      children[i].style.display = 'none'
    }

    setVisibleCount(count)
  }, [maxVisibleCount, showAllChips, scrollArrows, items.length])

  const scheduleAdjustLayout = useCallback(() => {
    queueMicrotask(() => adjustLayout())
  }, [adjustLayout])

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return

    scheduleAdjustLayout()
    const observer = new ResizeObserver(scheduleAdjustLayout)
    const parent = container.parentElement
    if (parent) observer.observe(parent)
    return () => observer.disconnect()
  }, [scheduleAdjustLayout])

  useLayoutEffect(() => {
    scheduleAdjustLayout()
  }, [items, scheduleAdjustLayout, width])

  // scrollArrows 模式:监测单行 chip 是否滚到两端,决定箭头 disabled 态。
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const checkScrollOverflow = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setCanScrollLeft(scrollLeft > 1)
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1)
  }, [])

  // 返回真实承担横向滚动的元素:沿祖先向上找第一个 overflow-x 为 auto/scroll 且
  // 内容宽于视口的容器。scrollArrows 模式是 Tabs.List 自身;移动端是外层
  // .navChipList —— 内层 tablist 虽更宽却是 overflow visible(不可滚),只看宽度
  // 会误判。与 scrollIntoView 会自己爬祖先不同,读写滚动位置必须显式拿到这个
  // 容器。item 渲染完成后才有效。
  const getScroller = useCallback((): HTMLElement | null => {
    let el: HTMLElement | null = containerRef.current
    while (el) {
      const { overflowX } = getComputedStyle(el)
      if ((overflowX === 'auto' || overflowX === 'scroll') && el.scrollWidth > el.clientWidth) {
        return el
      }
      el = el.parentElement
    }
    return null
  }, [])

  // items 首次渲染前 clientWidth 仍是 0,会误判 canScrollRight=true 让箭头在
  // 无溢出时闪现——所以 deps 里带上 items,等 chip 落地后再重判一轮。
  useEffect(() => {
    if (!scrollArrows) return
    const el = containerRef.current
    if (!el) return
    checkScrollOverflow()
    el.addEventListener('scroll', checkScrollOverflow)
    const observer = new ResizeObserver(checkScrollOverflow)
    observer.observe(el)
    const parent = el.parentElement
    if (parent) observer.observe(parent)
    return () => {
      el.removeEventListener('scroll', checkScrollOverflow)
      observer.disconnect()
    }
  }, [scrollArrows, checkScrollOverflow, width, items])

  // 首次挂载时把选中 chip 滚入可视区。
  //
  // 只滚一次:定位成功后才置位(无 scrollTrackKey 走组件级 ref,有则写模块级
  // memo)—— 之后的 activeValue 变化不再滚动,把用户停留的滚动位置留在原地。
  // 微任务 + 下一帧各执行一次,给字体/图片加载后的布局再校准。
  useEffect(() => {
    const alreadyScrolled = scrollTrackKey
      ? (scrollMemosByTrackKey.get(scrollTrackKey)?.scrolledOnce ?? false)
      : didInitialScrollRef.current
    if (alreadyScrolled || !activeValue) return

    const scrollIntoViewActiveChip = () => {
      // 定位必须真正发生才置位;否则(items 尚未渲染 / data-active 未落地)
      // 保持未置位,等下次 activeValue 变化时重试一次。
      const container = containerRef.current
      const activeChip = container?.querySelector<HTMLElement>('[data-active="true"]')
      if (!container || !activeChip) return
      const scroller = getScroller()
      if (!scroller) return
      if (scrollTrackKey) {
        scrollMemosByTrackKey.set(scrollTrackKey, {
          scrolledOnce: true,
          scrollLeft: scroller.scrollLeft,
        })
      } else {
        didInitialScrollRef.current = true
      }
      const sr = scroller.getBoundingClientRect()
      const ar = activeChip.getBoundingClientRect()
      if (ar.left >= sr.left - 1 && ar.right <= sr.right + 1) return
      activeChip.scrollIntoView({
        block: 'nearest',
        inline: 'center',
        behavior: 'smooth',
      })
    }

    queueMicrotask(scrollIntoViewActiveChip)
    const raf = requestAnimationFrame(scrollIntoViewActiveChip)
    return () => cancelAnimationFrame(raf)
  }, [activeValue, scrollTrackKey, getScroller])

  // scrollTrackKey 实例在重挂载时:跳过首滚,直接恢复上次停留的滚动位置
  // (重挂载会重建 DOM、scrollLeft 归零,不恢复的话条会跳回起点)。先同步试
  // 一次,再交一帧后重试兜底。
  useLayoutEffect(() => {
    if (!scrollTrackKey) return
    const applyRestore = () => {
      const memo = scrollMemosByTrackKey.get(scrollTrackKey)
      if (!memo?.scrolledOnce) return
      const scroller = getScroller()
      if (!scroller) return
      // 布局未就绪时别把目标值写进被 clamp 的容器,等下一帧再试。
      if (memo.scrollLeft > 0 && scroller.scrollWidth <= scroller.clientWidth) return
      scroller.scrollLeft = memo.scrollLeft
    }
    applyRestore()
    const raf = requestAnimationFrame(applyRestore)
    return () => cancelAnimationFrame(raf)
  }, [scrollTrackKey, getScroller])

  // 外部触发(抽屉内选中收起等):选中值一变就把 active chip 滚到条左侧。
  // 显式算 scrollLeft 而不是 `scrollIntoView(inline: 'start')` —— 后者对齐
  // 滚动容器的 border-box 左缘,会落在 `--nav-chip-list-leading-inset`(mobile
  // 条给 more 按钮让位的 46px padding)里被按钮盖住。这里手动减掉 padding,
  // 让 chip 落在 content 区左缘、按钮之后。已在屏内的 chip 也一并左对齐——
  // 「显示出来的屏幕左侧」。
  useEffect(() => {
    if (!scrollToActiveTrigger) return
    const container = containerRef.current
    const activeChip = container?.querySelector<HTMLElement>('[data-active="true"]')
    const scroller = getScroller()
    if (!container || !activeChip || !scroller) return
    const padLeft = Number.parseFloat(getComputedStyle(scroller).paddingLeft) || 0
    const target =
      scroller.scrollLeft +
      activeChip.getBoundingClientRect().left -
      scroller.getBoundingClientRect().left -
      padLeft
    scroller.scrollTo({ left: target, behavior: 'smooth' })
  }, [scrollToActiveTrigger, getScroller])

  // 跟踪用户滚动,实时更新 memo 位置。位置不写回值下限,避免卸载时把 0 覆盖掉
  // 已保存的有效位置 —— 由滚动事件持有的实时值已经足够新。
  useEffect(() => {
    if (!scrollTrackKey) return
    const scroller = getScroller()
    if (!scroller) return
    const save = () => {
      scrollMemosByTrackKey.set(scrollTrackKey, {
        scrolledOnce: scrollMemosByTrackKey.get(scrollTrackKey)?.scrolledOnce ?? false,
        scrollLeft: scroller.scrollLeft,
      })
    }
    scroller.addEventListener('scroll', save, { passive: true })
    return () => scroller.removeEventListener('scroll', save)
  }, [scrollTrackKey, getScroller])

  // 逐 chip 滚动:每次点击正好滚出/滚入一个完整胶囊。把「视口边缘上被部分
  // 裁切」的 chip 也算进来——右滚取第一个右缘超出视口的 chip,滚到与视口右缘
  // 对齐;左滚取最后一个左缘在视口外的 chip,滚到与左缘对齐。避免半露的 chip
  // 被跳过导致一步滚出两个。
  const scrollToNext = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const er = el.getBoundingClientRect()
    const nextItem = Array.from(el.children as HTMLCollectionOf<HTMLElement>).find(
      item => item.getBoundingClientRect().right > er.right + 1,
    )
    if (!nextItem) return
    const ir = nextItem.getBoundingClientRect()
    el.scrollBy({ left: ir.right - er.right, behavior: 'smooth' })
  }, [])

  const scrollToPrevious = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    const er = el.getBoundingClientRect()
    const chipItems = Array.from(el.children as HTMLCollectionOf<HTMLElement>)
    const prevItem = chipItems.findLast(item => item.getBoundingClientRect().left < er.left - 1)
    if (!prevItem) return
    const ir = prevItem.getBoundingClientRect()
    el.scrollBy({ left: ir.left - er.left, behavior: 'smooth' })
  }, [])

  const moreItems = showAllChips || scrollArrows ? [] : items.slice(visibleCount)

  if (items.length === 0) return null

  const resolvedAriaLabel = ariaLabel ?? t('categoryNavigationAria')

  return (
    <Flex
      component="nav"
      aria-label={resolvedAriaLabel}
      gap="2xs"
      className={clsx(
        styles.navChipList,
        showAllChips && styles.navChipListAllVisible,
        scrollArrows && styles.navChipListScrollArrows,
        mobileVariant === 'pill' && styles.navChipListPillMobile,
      )}
      data-scrollable={scrollArrows && (canScrollLeft || canScrollRight) ? 'true' : undefined}
      style={{ '--nav-chip-list-leading-inset': `${mobileLeadingInset}px` } as CSSProperties}
      align="center"
      wrap={showAllChips ? 'wrap' : 'nowrap'}
    >
      {scrollArrows && (
        <Box
          component="button"
          type="button"
          aria-label={t('scrollBackAria')}
          className={styles.chevronIndicator}
          disabled={!canScrollLeft}
          onClick={scrollToPrevious}
        >
          <ChevronRightIcon
            width={20}
            height={20}
            fill="var(--content-primary)"
            style={{ transform: 'rotate(180deg)' }}
          />
        </Box>
      )}
      <Tabs
        value={activeValue}
        // Tabs.onChange is a no-op — selection is driven by each chip's onClick.
        onChange={() => {}}
        variant="default"
        classNames={{ root: styles.tabsRoot, tabLabel: styles.tabLabel }}
      >
        <Tabs.List ref={containerRef} className={styles.itemsContainer}>
          {items.map((item, index) => {
            return (
              <Tabs.Tab
                key={item.slug || `__chip-${index}__`}
                value={item.slug}
                renderRoot={props => <button {...props} type="button" onClick={item.onClick} />}
                style={
                  !isMobile && !showAllChips && !scrollArrows && index >= maxVisibleCount
                    ? { display: 'none' }
                    : undefined
                }
                className={styles.tab}
              >
                {item.showLiveDot && (
                  <LiveIcon
                    width={16}
                    height={16}
                    className={styles.liveIcon}
                    data-testid="nav-live-dot"
                    aria-hidden
                  />
                )}
                {item.iconUrl && (
                  <NextBaseImage
                    src={item.iconUrl}
                    alt=""
                    aria-hidden
                    width={16}
                    height={16}
                    unoptimized
                    fallback={null}
                    className={styles.chipIcon}
                  />
                )}
                {item.label}
                {item.countLabel && <span className={styles.chipCount}>{item.countLabel}</span>}
              </Tabs.Tab>
            )
          })}
        </Tabs.List>
      </Tabs>

      {scrollArrows && (
        <Box
          component="button"
          type="button"
          aria-label={t('scrollForwardAria')}
          className={styles.chevronIndicator}
          disabled={!canScrollRight}
          onClick={scrollToNext}
        >
          <ChevronRightIcon width={20} height={20} fill="var(--content-primary)" />
        </Box>
      )}

      {moreItems.length > 0 && !isMobile && !scrollArrows && (
        <HoverCard withinPortal={false}>
          <HoverCard.Target>
            <Box className={styles.moreTrigger}>
              <Text variant="subtitle-emphasis">
                {t('more')}{' '}
                <ChevronDownIcon
                  width={20}
                  height={20}
                  fill="var(--content-primary)"
                  className={styles.chevronIcon}
                />
              </Text>
            </Box>
          </HoverCard.Target>
          <HoverCard.Dropdown p="sm">
            <SimpleGrid
              cols={2}
              spacing="2xs"
              verticalSpacing="2xs"
              style={{ gridTemplateColumns: 'repeat(2, max-content)' }}
            >
              {moreItems.map(item => (
                <Box key={item.slug}>
                  <button type="button" className={styles.moreDropdownItem} onClick={item.onClick}>
                    <Text variant="subtitle-emphasis" component="span">
                      {item.label}
                    </Text>
                  </button>
                </Box>
              ))}
            </SimpleGrid>
          </HoverCard.Dropdown>
        </HoverCard>
      )}
    </Flex>
  )
}
